"use client";

import { useTranslations } from "next-intl";
import { Fragment, useContext } from "react";
import { useDateFormatter } from "react-aria";

import { calculatePaymentPlan, type Payment } from "@trip/common";
import { SETTLEMENT_CURRENCY } from "@trip/constants";

import {
  CurrencyAmount,
  SettlementCurrencyAmount,
} from "~/components/currency-amount";
import { CurrencyContext } from "~/components/currency-provider";
import { HR } from "~/components/typography";

export function PaymentPlan({ date, total }: { date: Date; total: number }) {
  const t = useTranslations("TripDetails");
  const tCommon = useTranslations("Common");
  const { currency } = useContext(CurrencyContext);
  const [dueNowPayment, ...invoicedPayments] = calculatePaymentPlan(
    date,
    total,
  );
  const formatDate = useDateFormatter({
    dateStyle: "medium",
    timeZone: "UTC",
  });

  if (invoicedPayments.length === 0) {
    return null;
  }
  return (
    <div>
      <div className="mb-2 font-medium">{t("paymentPlan")}</div>
      <div className="grid grid-cols-[auto_max-content] gap-2 text-sm">
        <div>
          {t("confirmationFee", { percentage: dueNowPayment.percentage })}
        </div>
        <div className="text-right">
          <div className="font-medium">
            <SettlementCurrencyAmount amount={dueNowPayment.amount} />
          </div>
          {SETTLEMENT_CURRENCY !== currency.code ? (
            <div className="text-xs text-neutral-500">
              {tCommon("approximate")}
              <CurrencyAmount amount={dueNowPayment.amount} />
            </div>
          ) : null}
        </div>
        {invoicedPayments.map((payment: Payment) => (
          <Fragment key={payment.date.valueOf()}>
            <div>
              {t("paymentDateWithPercentage", {
                date: formatDate.format(payment.date),
                percentage: payment.percentage,
              })}
            </div>
            <div className="text-right">
              <div className="font-medium">
                <SettlementCurrencyAmount amount={payment.amount} />
              </div>
              {SETTLEMENT_CURRENCY !== currency.code ? (
                <div className="text-xs text-neutral-500">
                  {tCommon("approximate")}
                  <CurrencyAmount amount={payment.amount} />
                </div>
              ) : null}
            </div>
          </Fragment>
        ))}
      </div>

      <HR className="my-4" />
    </div>
  );
}
